import React, { useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { Wind, CheckCircle2, AlertTriangle, AlertOctagon, Thermometer, Droplets, Sparkles, Gauge } from "lucide-react";

const ZONES = [
  { zone: "Ground Floor Atrium", floor: "GF",  co2: 650,  pm25: 18, temp: 24.5, hum: 58, voc: "Good",     status: "Healthy"  },
  { zone: "Food Court",          floor: "L2",  co2: 1120, pm25: 48, temp: 27.2, hum: 65, voc: "Moderate", status: "Moderate" },
  { zone: "Parking Basement B1", floor: "B1",  co2: 890,  pm25: 72, temp: 31.1, hum: 71, voc: "Poor",     status: "Poor"     },
  { zone: "Server Room",         floor: "L3",  co2: 520,  pm25: 12, temp: 21.0, hum: 45, voc: "Good",     status: "Healthy"  },
  { zone: "Cinema Hall",         floor: "L4",  co2: 980,  pm25: 38, temp: 23.8, hum: 62, voc: "Moderate", status: "Moderate" },
  { zone: "Office Wing East",    floor: "L3",  co2: 710,  pm25: 22, temp: 25.5, hum: 55, voc: "Good",     status: "Healthy"  },
];

const CO2_TREND = Array.from({ length: 24 }, (_, h) => ({
  hour: `${String(h).padStart(2, "0")}:00`,
  co2: Math.round(620 + Math.sin((h - 6) / 3) * 180 + (h > 12 && h < 22 ? 140 : 0) + Math.random() * 40),
}));

const STATUS_TONE = {
  Healthy:  { dot: "bg-emerald-500", text: "text-emerald-300", bg: "bg-emerald-500/15", icon: CheckCircle2 },
  Moderate: { dot: "bg-amber-500",   text: "text-amber-300",   bg: "bg-amber-500/15",   icon: AlertTriangle },
  Poor:     { dot: "bg-red-500",     text: "text-red-300",     bg: "bg-red-500/15",     icon: AlertOctagon  },
};

function Kpi({ icon: Icon, label, value, unit, tone, sub }) {
  const gradients = {
    green: "from-emerald-400 to-emerald-600",
    amber: "from-amber-400 to-orange-500",
    red:   "from-red-500 to-pink-600",
    blue:  "from-sky-400 to-indigo-500",
    purple:"from-purple-500 to-indigo-500",
  };
  return (
    <div className="glass rounded-lg p-5">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradients[tone] || gradients.blue} flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-gray-900" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      </div>
      <div className="mt-3">
        <span className="text-3xl font-extrabold tracking-tight">{value}</span>
        {unit && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function IAQMonitor() {
  const avg = useMemo(() => {
    const avg1 = (k) => Math.round(ZONES.reduce((s, z) => s + z[k], 0) / ZONES.length);
    const avgF = (k, d = 1) => (ZONES.reduce((s, z) => s + z[k], 0) / ZONES.length).toFixed(d);
    return {
      co2: avg1("co2"),
      pm25: avg1("pm25"),
      temp: avgF("temp"),
      hum: avgF("hum", 0),
    };
  }, []);

  const co2Tone = avg.co2 < 800 ? "green" : avg.co2 <= 1000 ? "amber" : "red";
  const pm25Tone = avg.pm25 < 35 ? "green" : avg.pm25 <= 75 ? "amber" : "red";
  const vocCount = { Good: 0, Moderate: 0, Poor: 0 };
  ZONES.forEach((z) => (vocCount[z.voc] = (vocCount[z.voc] || 0) + 1));
  const vocTone = vocCount.Poor ? "red" : vocCount.Moderate ? "amber" : "green";

  return (
    <div className="space-y-6" data-testid="iaq-monitor-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
              <Wind className="w-8 h-8 text-sky-400" strokeWidth={2.5} /> IAQ Monitor
            </h1>
            <span className="px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest">Live</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Indoor Air Quality · 6 zones · auto-refresh 30s</p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest bg-sky-500/10 text-sky-300 border border-sky-500/20 font-bold self-start">IS 13410 / ASHRAE 62.1 Compliance</span>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi icon={Gauge}       label="Avg CO₂"         value={avg.co2}   unit="ppm"  tone={co2Tone}  sub="Target < 800 ppm" />
        <Kpi icon={Sparkles}    label="Avg PM2.5"       value={avg.pm25}  unit="µg/m³" tone={pm25Tone} sub="Target < 35" />
        <Kpi icon={Thermometer} label="Avg Temperature" value={avg.temp}  unit="°C"   tone="blue" />
        <Kpi icon={Droplets}    label="Avg Humidity"    value={avg.hum}   unit="%"    tone="blue" />
        <Kpi icon={Wind}        label="VOC Index"       value={vocCount.Poor ? "Poor" : vocCount.Moderate ? "Moderate" : "Good"} tone={vocTone} sub={`${vocCount.Good} good · ${vocCount.Moderate} mod · ${vocCount.Poor} poor`} />
      </div>

      {/* ZONE TABLE */}
      <div className="glass rounded-lg overflow-hidden" data-testid="iaq-zone-table">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-bold text-lg">Zone-wise IAQ Readings</h2>
          <span className="text-[11px] text-gray-500">Last updated: just now</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">Zone</th>
                <th className="text-left py-3">Floor</th>
                <th className="text-right py-3">CO₂</th>
                <th className="text-right py-3">PM2.5</th>
                <th className="text-right py-3">Temp</th>
                <th className="text-right py-3">Humidity</th>
                <th className="text-left py-3 pl-4">VOC</th>
                <th className="text-center py-3">Status</th>
                <th className="text-right px-5 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {ZONES.map((z) => {
                const t = STATUS_TONE[z.status];
                const Icon = t.icon;
                return (
                  <tr key={z.zone} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`iaq-zone-${z.zone.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="px-5 py-3 font-semibold">{z.zone}</td>
                    <td className="py-3 text-gray-500">{z.floor}</td>
                    <td className={`py-3 text-right font-bold tabular-nums ${z.co2 > 1000 ? "text-red-300" : z.co2 > 800 ? "text-amber-300" : "text-emerald-300"}`}>{z.co2}</td>
                    <td className={`py-3 text-right font-bold tabular-nums ${z.pm25 > 75 ? "text-red-300" : z.pm25 > 35 ? "text-amber-300" : "text-emerald-300"}`}>{z.pm25}</td>
                    <td className="py-3 text-right tabular-nums">{z.temp}°C</td>
                    <td className="py-3 text-right tabular-nums">{z.hum}%</td>
                    <td className="py-3 pl-4">{z.voc}</td>
                    <td className="py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${t.bg} ${t.text} text-[10px] uppercase tracking-widest font-bold`}>
                        <Icon className="w-3 h-3" /> {z.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-[11px] text-gray-500">2 min ago</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass rounded-lg p-5">
          <h3 className="font-semibold mb-3">CO₂ Trend — Last 24 Hours</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={CO2_TREND}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#64748b" fontSize={10} interval={2} />
              <YAxis stroke="#64748b" fontSize={10} domain={[400, 1400]} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <ReferenceLine y={1000} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "Warning 1000 ppm", position: "right", fill: "#ef4444", fontSize: 10 }} />
              <ReferenceLine y={800} stroke="#f59e0b" strokeDasharray="5 5" />
              <Line type="monotone" dataKey="co2" stroke="#38bdf8" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="glass rounded-lg p-5">
          <h3 className="font-semibold mb-3">PM2.5 by Zone</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={ZONES.map((z) => ({ zone: z.zone.split(" ")[0], pm25: z.pm25 }))}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="zone" stroke="#64748b" fontSize={10} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <ReferenceLine y={75} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "Unhealthy", position: "right", fill: "#ef4444", fontSize: 10 }} />
              <ReferenceLine y={35} stroke="#f59e0b" strokeDasharray="5 5" />
              <Bar dataKey="pm25" fill="#ff6b35" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ALERTS */}
      <div className="glass rounded-lg p-5" data-testid="iaq-alerts">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={2.5} /> Active IAQ Alerts</h3>
        <div className="space-y-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <span className="px-2 py-0.5 rounded-full bg-red-500/25 text-red-200 text-[10px] uppercase tracking-widest font-bold">Critical</span>
            <div className="flex-1">
              <div className="font-bold text-gray-900">Parking Basement B1 — CO₂ exceeded safe threshold</div>
              <div className="text-xs text-gray-500 mt-0.5">890 ppm recorded · Ventilation check required · Phoenix Marketcity Mall</div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <span className="px-2 py-0.5 rounded-full bg-orange-500/25 text-orange-200 text-[10px] uppercase tracking-widest font-bold">High</span>
            <div className="flex-1">
              <div className="font-bold text-gray-900">Food Court (Level 2) — PM2.5 degraded</div>
              <div className="text-xs text-gray-500 mt-0.5">48 µg/m³ recorded · Above recommended 35 µg/m³ · Schedule filter cleaning</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
